import { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Network } from '@capacitor/network';
import { onlineManager } from '@tanstack/react-query';
import { getSearchCardMinSnapPx } from '../../constants';

type NetworkStatusAlertProps = {
  containerClassName?: string;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'absolute',
    bottom: `${getSearchCardMinSnapPx() + 16}px`,
    width: '100%',
    padding: theme.spacing(0, 2),
    pointerEvents: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000000000000,
  },
  alert: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 500,
    boxShadow: theme.shadows[3],
    minWidth: 343,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  icon: {
    paddingTop: 9,
  },
}));

const NetworkStatusAlert = (
  props: NetworkStatusAlertProps,
): JSX.Element | null => {
  const { containerClassName } = props;
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [connected, setConnected] = useState(true);

  /**
   * Initializes the online status based on the actual network connection
   * when the component mounts. This is crucial because, according to the
   * TanStack documentation, `onlineManager.isOnline()` always starts as
   * true. We need to use the Network plugin to get the true initial connection
   * status and accurately update the `onlineManager` to avoid starting the
   * app in an incorrect "online" state when the device is actually offline.
   * Any subsequent changes in online status (including potential false negatives
   * if the initial check is somehow incorrect) will be handled by the
   * subscription to the onlineManager in the useEffect below.
   */
  useEffect(() => {
    const setInitialConnectionStatus = async (): Promise<void> => {
      const status = await Network.getStatus();
      onlineManager.setOnline(status.connected);
    };

    setInitialConnectionStatus();
  }, []);

  useEffect(() => {
    const unsubscribe = onlineManager.subscribe((isOnline) => {
      setConnected(isOnline);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (connected) return null;

  return (
    <div className={cx(classes.container, containerClassName)}>
      <Alert
        severity="warning"
        classes={{
          root: classes.alert,
          icon: classes.icon,
        }}
        aria-labelledby="network-status-message"
      >
        <span id="network-status-message">
          {t('networkStatus.disconnected')}
        </span>
      </Alert>
    </div>
  );
};

export default NetworkStatusAlert;
